import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MapPin, ArrowRight, Dumbbell } from "lucide-react";
import api from "@/lib/api";
import { sportImage, loginTo } from "./data";

export default function FeaturedFacilities() {
  const [facs, setFacs] = useState(null);
  useEffect(() => {
    api.get("/public/facilities?limit=8")
      .then((r) => setFacs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFacs([]));
  }, []);

  if (facs === null) return null;
  const items = facs.slice(0, 8);

  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Featured sports facilities</h2>
          <p className="mt-2 text-sm text-muted-foreground">Verified grounds, courts and pools you can book by the slot.</p>
        </div>
        {items.length > 0 && (
          <Link to="/browse?cat=facilities" data-testid="view-all-facilities"
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold transition-colors hover:border-primary/50 hover:text-primary">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>

      {items.length === 0 ? (
        <div className="mt-7 flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card/50 py-16 text-center" data-testid="facilities-empty">
          <Dumbbell className="mb-3 h-10 w-10 text-muted-foreground" />
          <p className="font-display text-xl font-semibold">No facilities listed yet</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">Be the first to list your ground, court or pool and start taking slot bookings on SPORA1.</p>
          <Link to={loginTo("/app/facilities")} data-testid="list-facility-cta"
            className="mt-5 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[#FF8533]">
            List Your Facility
          </Link>
        </div>
      ) : (
        <div className="mt-7 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((f, i) => (
            <motion.div key={f.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.35, delay: (i % 4) * 0.05 }}
              className="group overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={f.image_url || sportImage(f.sport)} alt={f.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {f.rating != null && (
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-xs font-semibold text-foreground shadow">
                    <Star className="h-3 w-3 fill-primary text-primary" /> {f.rating}
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="truncate font-display text-lg font-semibold leading-tight">{f.name}</h3>
                <p className="mt-0.5 truncate text-sm capitalize text-primary">{f.sport || "Multi-sport"}</p>
                {f.city && <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {f.city}</p>}
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {f.min_price > 0
                      ? <><span className="font-display text-xl font-bold text-foreground">₹{f.min_price}</span>/slot</>
                      : <span className="font-display text-base font-bold text-emerald-600">Free</span>}
                  </p>
                  <Link to={`/browse/facilities/${f.id}`} data-testid={`book-${(f.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    className="rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[#FF8533]">
                    Book Now
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}
